/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/addons/addon_helpers.test.ts
 */

//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import { isPill, isPlusUni, isXMODDevice, real_id } from '../interface_helpers';
import { getLoraAddonStatus } from 'src/helpers/addons/addon_lora_helpers';
import { getPeripheralDevice, isPeripheralDevice } from './addon_device_helpers';
import { getAddonsSupported, isProSensorAddon } from './addon_support_helpers';
import { convertToUnit } from '../units_helpers';
//Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Types
import type { Device } from 'src/types/Device';
import type {
	DigitalInputStatus,
	Peripheral,
	PeripheralInputSettings,
	PeripheralSettings,
	PeripheralTypes,
	Sensor,
} from 'src/types/Peripherals';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

export const hasAddon = (device: Device) => {
	return getAddonsSupported(device?.type)?.length > 0;
};

export const hasAddonPeripherals = (device: Device, hidePeripheralInParent = false) => {
	return hasAddon(device) && getAddonStatus(device, [], false, hidePeripheralInParent).length > 0;
};

const addonOutputTypes = {
	switch: {
		name: 'switch',
		icon: 'far fa-toggle-on',
		unit: '',
		alias: 'Output',
		valueAttr: 'output',
	},
};

export const addonInputTypes = {
	temperature: {
		name: 'temperature',
		icon: 'fas fa-thermometer-three-quarters',
		unit: 'C',
		alias: 'Temperature sensor',
		valueAttr: 'tC',
		thrMin: 0.1,
		thrMax: 5,
		offsetMin: -50,
		offsetMax: 50,
	},
	humidity: {
		name: 'humidity',
		icon: 'fas fa-humidity',
		unit: '%',
		alias: 'Humidity sensor',
		valueAttr: 'rh',
		thrMin: 1,
		thrMax: 20,
		offsetMin: -50,
		offsetMax: 50,
	},
	voltmeter: {
		name: 'voltmeter',
		icon: 'fas fa-gauge-circle-bolt',
		unit: 'V',
		xunit: '',
		alias: 'Voltmeter',
		valueAttr: 'voltage',
		thrMin: 0.1,
		thrMax: 5,
	},
	input: {
		analog: {
			name: 'analog',
			icon: 'fas fa-gauge-simple',
			unit: '%',
			xunit: '',
			alias: 'Analog Input',
			valueAttr: 'percent',
			thrMin: 1,
			thrMax: 50,
		},
		switch: {
			name: 'digital',
			icon: 'fas fa-scrubber',
			unit: '',
			alias: 'Digital Input',
		},
		button: {
			name: 'digital',
			icon: '',
			unit: '',
			alias: 'Digital Input',
		},
	},
};

export const getAddonStatus = (
	device: Device,
	interface_data = [],
	checkIsEnable = false,
	hidePeripheralInParent = false,
): Peripheral[] => {
	const peripheralDevice = isPeripheralDevice(device?.type);
	if (peripheralDevice || hasAddon(device)) {
		const id = real_id(device.id),
			deviceSettings = devices_settings.getByRealId(id);
		if (!deviceSettings) return interface_data;
		const plusUni = isPlusUni(device?.type) || isPlusUni(deviceSettings?.device?.type);
		const pill = isPill(device?.type) || isPill(deviceSettings?.device?.type);
		const genericAddon = plusUni || pill;
		const addonType = genericAddon ? 'sensor' : deviceSettings.sys?.device?.addon_type;
		if (addonType == 'LoRa') {
			return hidePeripheralInParent && get(devices)[`${id}_100`]
				? interface_data
				: getLoraAddonStatus(device, interface_data);
		} else if (isXMODDevice(device?.type) && device?.mode === 'cct') {
			return interface_data;
		} else if (addonType) {
			const hasDigitalOutput = pill || isProSensorAddon(device);
			const hasSwitchAddon =
				addonType == 'prooutput' ||
				hasDigitalOutput ||
				(peripheralDevice && device.category === 'relay');
			const inputTypes = hasSwitchAddon
				? hasDigitalOutput
					? { ...addonInputTypes, ...addonOutputTypes }
					: addonOutputTypes
				: addonInputTypes;
			return getPeripheralStatus(
				device,
				inputTypes,
				interface_data,
				checkIsEnable,
				peripheralDevice,
				hidePeripheralInParent,
			);
		}
	}
	return interface_data;
};

const getPeripheralComponents = (inputTypes: any, deviceSettings: DeviceSettingsG2) => {
	const peripherals = [];
	Object.keys(inputTypes).forEach((type) => {
		Object.keys(deviceSettings || {})
			.sort()
			.forEach((component) => {
				if (RegExp(`^${type}:\\d{3}$`).test(component)) {
					peripherals.push({
						component,
						type,
						settings: deviceSettings[component],
					});
				}
			});
	});
	return peripherals;
};

const getPeripheralStatus = (
	device: Device,
	inputTypes: any,
	interface_data = [],
	checkIsEnable = false,
	peripheralDevice = false,
	hidePeripheralInParent = false,
): Peripheral[] => {
	const $format = get(format);
	const id = real_id(device.id);
	const deviceStatus = devices_status.getByRealId(id);
	const deviceSettings = devices_settings.getByRealId(id);
	const peripheralsMap = getPeripheralComponents(inputTypes, deviceSettings);
	peripheralsMap.forEach((item) => {
		const { component, type, settings } = item;
		const inputType = settings.type || 'none';
		const extid = settings.id;
		const port = type == 'input' ? inputTypes[type]?.[inputType] : inputTypes[type];
		const unit =
			type == 'temperature' ? deviceSettings?.sys?.ui_data?.temperature_unit || 'C' : port.unit;
		const valueAttr = type == 'temperature' ? `t${unit}` : port.valueAttr;
		let digitalInputState = /^(switch|button)$/.test(inputType)
			? getDigitalInputButtonState(device, deviceStatus, inputType, extid)
			: null;
		let value = deviceStatus?.[component]?.[valueAttr];
		if (type === 'switch') {
			const dStatus = digitalInputStatuses()[0];
			digitalInputState = {
				label: value ? dStatus.label_on : dStatus.label_off,
				icon: value ? dStatus.icon_on : dStatus.icon_off,
			};
		}
		const alias = `${port.alias} (${extid})`;
		const sensor: Sensor = {
			id,
			type,
			unit,
			component,
			inputType,
			value:
				digitalInputState?.label ||
				convertToUnit(
					value == null || isNaN(value) ? 'N/A' : value,
					`${type == 'temperature' ? '°' : ''}${unit}`,
				),
			extid,
			xunit: '',
			alias,
			icon: digitalInputState?.icon || port.icon,
			label: $format(`addon_peripheral_${port.name}_sensor`, {
				values: { i: extid },
				default: port.alias,
			}),
			name: settings.name ? settings.name : alias,
		};
		if (port.name == 'analog' && settings?.xpercent?.unit) {
			let value = deviceStatus[component].xpercent || 0;
			value = value == null || isNaN(value) ? 'N/A' : value;
			sensor.xunit = settings.xpercent.unit;
			sensor.value = `${value} ${sensor.xunit}`;
		} else if (port.name == 'voltmeter' && settings?.xvoltage?.unit) {
			let value = deviceStatus[component]?.xvoltage || 0;
			value = value == null || isNaN(value) ? 'N/A' : value;
			sensor.xunit = settings.xvoltage.unit;
			sensor.value = `${value} ${sensor.xunit}`;
		}
		if (!checkIsEnable || settings.enable !== false) {
			const peripheral: Peripheral = {
				name: `${port.name}:${extid}`,
				peripheralType: type as PeripheralTypes,
				peripheralInputType: port.name,
				icon: sensor.icon,
				value: sensor.value,
				deviceID: id,
				sensor,
				settings,
				advLabel: type === 'switch',
			};
			if (peripheralDevice || !hidePeripheralInParent || !getPeripheralDevice(id, peripheral)) {
				interface_data.push(peripheral);
			}
		}
	});
	return interface_data;
};

export const getPlusUniCountStatus = (
	device: Device,
	interface_data = [],
	checkIsEnable = false,
): Peripheral[] => {
	if (!isPlusUni(device?.type)) return interface_data;
	try {
		const id = real_id(device.id),
			component = 'input:2',
			deviceSettings = devices_settings.getByRealId(device.id),
			deviceStatus = devices_status.getByRealId(device.id);

		if (deviceStatus && deviceSettings) {
			const status = deviceStatus[component];
			const settings: PeripheralInputSettings = deviceSettings[component];

			if (!status) {
				console.log('No status for Plus UNI Counter');
				return interface_data;
			}

			if (!settings) {
				console.log('No settings for Plus UNI Counter');
				return interface_data;
			}

			let unit = 'p',
				xunit = settings?.xcounts?.unit || '',
				freqUnit = 'Hz',
				freqXUnit = settings?.xfreq?.unit || '',
				value = `${status?.counts?.total || 0} ${unit}`,
				freq = `${status?.freq || 0} ${freqUnit}`;
			if (xunit) {
				const xtotal = status?.counts?.xtotal;
				value = `${xtotal == null || isNaN(xtotal) ? 'N/A' : xtotal} ${xunit}`;
			}
			if (freqXUnit) {
				const xfreq = status?.xfreq;
				freq = `${xfreq == null || isNaN(xfreq) ? 'N/A' : xfreq} ${freqXUnit}`;
			}

			const $format = get(format);
			const alias = 'Input (2)';
			const sensor: Sensor = {
				id,
				type: 'input',
				unit,
				xunit,
				freqUnit,
				freqXUnit,
				component,
				inputType: 'count|freq',
				value,
				extid: settings?.id || 2,
				alias,
				icon: 'far fa-gauge-circle-plus',
				name: settings?.name ? settings.name : alias,
				label: $format(`plus_uni_counter_input`, {
					default: alias,
				}),
			};

			if (!checkIsEnable || settings.enable !== false) {
				const peripheral: Peripheral = {
					name: 'count',
					peripheralType: 'input',
					peripheralInputType: 'count',
					icon: sensor.icon,
					value: sensor.value,
					deviceID: id,
					sensor,
					freq,
					freqIcon: 'fas fa-wave-square',
					settings: settings as PeripheralSettings,
				};
				interface_data.push(peripheral);
			}
		}
		return interface_data;
	} catch (error) {
		console.log('Error getPlusUniCountStatus', error);
		return interface_data;
	}
};

export const digitalInputStatuses = (): DigitalInputStatus[] => {
	const $format = get(format);
	return [
		{
			value: 0,
			icon: 'fas fa-toggle-on',
			icon_on: 'fas fa-toggle-on',
			icon_off: 'fas fa-toggle-off',
			label_on: $format('on', { default: 'on' }),
			label_off: $format('off', { default: 'off' }),
			title: $format('icon_name_basic', { default: 'Basic' }),
		},
		{
			value: 1,
			icon: 'fas fa-door-open',
			icon_on: 'fas fa-door-open',
			icon_off: 'fas fa-door-closed',
			label_on: $format('alarm_state_state_open', { default: 'open' }),
			label_off: $format('alarm_state_state_closed', { default: 'closed' }),
			title: $format('icon_name_door', { default: 'Door' }),
		},
		{
			value: 2,
			icon: 'fak fa-light-empty-garage',
			icon_on: 'fak fa-light-empty-garage',
			icon_off: 'fas fa-garage',
			label_on: $format('alarm_state_state_open', { default: 'open' }),
			label_off: $format('alarm_state_state_closed', { default: 'closed' }),
			title: $format('icon_name_garage', { default: 'Garage' }),
		},
		{
			value: 3,
			icon: 'fas fa-bell',
			icon_on: 'fas fa-bell-on',
			icon_off: 'fas fa-bell-slash',
			label_on: $format('state_chiming', { default: 'chiming' }),
			label_off: $format('state_silent', { default: 'silent' }),
			title: $format('icon_name_bell', { default: 'Bell' }),
		},
	];
};

export const hasSwitchAddon = (device: Device) => {
	return devices_settings.getByRealId(device?.id)?.sys?.device?.addon_type === 'prooutput';
};

export const getDigitalInputIcons = (device: Device, component: string): Record<string, number> => {
	const mainDevice: Device = get(devices)?.[real_id(device?.id)];
	if (mainDevice?.input_icons) {
		return mainDevice.input_icons;
	}
	const extSwitchIcon = Number(mainDevice?.ext_switch_icon);
	return { [component]: Number.isFinite(extSwitchIcon) ? extSwitchIcon : 0 };
};

const getDigitalInputButtonState = (device: Device, status: any, inputType: string, inputId: number) => {
	if (inputType == 'button') {
		const $format = get(format);
		const states = {
			S: $format('prop_short', { default: 'short pushed' }),
			SS: $format('prop_double_short', { default: 'double short pushed' }),
			SSS: $format('prop_triple_short', { default: 'triple short pushed' }),
			L: $format('prop_long', { default: 'long pushed' }),
		};
		const value = states[status?.[`v_eve:${inputId}`]?.ev];
		const label = value ? value : $format('off', { default: 'off' });
		const icon = 'far fa-scrubber';
		return { label, icon };
	}
	const component = `input:${inputId}`;
	const index = getDigitalInputIcons(device, component)[component] ?? 0;
	const inputStatus: DigitalInputStatus = digitalInputStatuses()[index];
	const isOn = status?.[component]?.state;
	const label = isOn ? inputStatus.label_on : inputStatus.label_off;
	const icon = isOn ? inputStatus.icon_on : inputStatus.icon_off;
	return { label, icon };
};

export const format_gen2_addons_for_scenes = (_addOns: any) => {
	let addOns = {};
	if (_addOns.length > 0) {
		_addOns.forEach((_addOn) => {
			if (!addOns[_addOn?.sensor?.type]) {
				addOns[_addOn?.sensor?.type] = [{ ..._addOn.sensor }];
			} else {
				addOns[_addOn?.sensor?.type].push({ ..._addOn.sensor });
			}
		});
	} else {
		addOns = undefined;
	}
	return addOns;
};
